import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { DASHBOARD_GUIDE } from '../lib/guide'
import {
  DEFAULT_SIZE_STEP,
  READING_SIZES,
  clampStep,
  countHits,
  currentHeadingId,
  normalizeQuery,
  outline,
  parse,
  pieces,
  type Block,
} from '../lib/guidetoc'

/**
 * The written user guide, rendered from the same text Pearl is given.
 *
 * There is deliberately no markdown library here. The guide uses four
 * constructs — headings, bullets, paragraphs and **bold** — and pulling a
 * dependency into the bundle to parse four constructs would be the more
 * expensive choice. The grammar lives in `guidetoc.ts` with the contents list,
 * the search and the scroll arithmetic, all of which are plain functions over
 * plain data so that they can be tested without a browser.
 *
 * If this page and Pearl ever disagree, something has been copied instead of
 * imported. They read `DASHBOARD_GUIDE`; nothing else describes these screens.
 *
 * WHAT THIS COMPONENT KEEPS, AND WHY IT IS ONLY THIS
 *
 * Scroll positions, a stored preference, and the effects that watch for them.
 * Every decision — which section you are in, which sections match, where the
 * highlights fall, whether a stored size is sane — is computed next door and
 * asserted in `test-guidetoc.mjs`.
 */

/** Where the reading size is remembered. */
const SIZE_KEY = 'agentcare.guide.size'

/**
 * How far below the top of the window a heading still counts as "the one you
 * are reading". Roughly one line of body text, so a heading half off the top
 * edge does not hand the highlight back to the section above it.
 */
const SPY_OFFSET = 90

/** Show "Back to top" once roughly a screenful has gone by. */
const BACK_TO_TOP_AFTER = 700

/**
 * The stored reading size, read once.
 *
 * In a `try` because `localStorage` is not always there to be read — a locked
 * down profile or private mode can throw on the getter itself rather than
 * returning null, and the guide failing to render because it could not
 * remember a font size would be a poor trade.
 */
function storedStep(): number {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY)
    if (raw === null) return DEFAULT_SIZE_STEP
    return clampStep(Number(raw))
  } catch {
    return DEFAULT_SIZE_STEP
  }
}

export default function Guide() {
  const blocks = useMemo(() => parse(DASHBOARD_GUIDE), [])
  const headings = useMemo(() => outline(blocks), [blocks])
  // Built once. `find` inside the render loop is one pass over every heading
  // for each of a thousand blocks, on every keystroke in the search box.
  const idByAt = useMemo(() => {
    const m = new Map<number, string>()
    for (const h of headings) m.set(h.at, h.id)
    return m
  }, [headings])

  const [raw, setRaw] = useState('')
  const query = normalizeQuery(raw)
  const hits = useMemo(() => countHits(blocks, headings, query), [blocks, headings, query])

  const [step, setStep] = useState(storedStep)
  const [current, setCurrent] = useState<string | null>(null)
  const [scrolled, setScrolled] = useState(false)
  /**
   * The contents list starts open on a wide window and closed on a narrow one,
   * where it would otherwise be the whole contents list between the reader and
   * the first paragraph. Read once, at mount: a window dragged narrower keeps whatever
   * the reader last chose, which is the less surprising of the two behaviours.
   */
  const [openToc, setOpenToc] = useState(() => {
    try {
      return window.matchMedia('(min-width: 980px)').matches
    } catch {
      return true
    }
  })

  const scale = READING_SIZES[clampStep(step)]

  useEffect(() => {
    try {
      window.localStorage.setItem(SIZE_KEY, String(step))
    } catch {
      // A size that cannot be remembered still works for this visit.
    }
  }, [step])

  /**
   * Where every heading sits on the page.
   *
   * Re-measured rather than remembered, because the reading size moves all of
   * them — a page at 1.75× is half again as long, and a contents list working
   * from the old positions would mark a section three or four away from the one
   * on screen. Measured on scroll, on resize, and whenever the size changes.
   */
  const spy = useRef<number | null>(null)
  const measure = useCallback(() => {
    const tops = headings
      .map((h) => {
        const el = document.getElementById(h.id)
        return el ? { id: h.id, top: el.getBoundingClientRect().top + window.scrollY } : null
      })
      .filter((t): t is { id: string; top: number } => t !== null)
    const y = window.scrollY
    const atBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 2
    setCurrent(currentHeadingId(tops, y, SPY_OFFSET, atBottom))
    setScrolled(y > BACK_TO_TOP_AFTER)
  }, [headings])

  useEffect(() => {
    /**
     * Coalesced onto a frame. `scroll` fires far faster than anything can be
     * usefully redrawn, and every one of these reads the position of every
     * heading in the document — left unthrottled that is a measurable stutter on
     * the one page whose whole job is being pleasant to read.
     */
    const onScroll = () => {
      if (spy.current !== null) return
      spy.current = window.requestAnimationFrame(() => {
        spy.current = null
        measure()
      })
    }
    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (spy.current !== null) window.cancelAnimationFrame(spy.current)
      spy.current = null
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
    /**
     * `query` is in here because highlighting changes the page's height. A
     * `<mark>` is a couple of pixels wider than the text it replaces, which can
     * rewrap a paragraph and move every heading below it — so the positions
     * measured a moment ago are wrong the instant a search is typed, and the
     * contents list marks the wrong section until the next scroll nudges it.
     */
  }, [measure, scale, query])

  /**
   * A link straight to a section — `/guide#tags` from a bookmark, an email, or
   * the address bar.
   *
   * The browser does this by itself for a page it already has, but this one is
   * not on screen when the address is resolved: React has not rendered the
   * headings yet, so there is nothing with that id to jump to and the browser
   * gives up silently. Once only, after the first paint.
   */
  const jumped = useRef(false)
  useEffect(() => {
    if (jumped.current) return
    jumped.current = true
    const hash = window.location.hash.replace(/^#/, '')
    if (!hash) return
    /**
     * `decodeURIComponent` THROWS on a stray percent sign — `/guide#100%` is
     * enough. This runs in an effect, so the throw is caught by the boundary
     * around the routes and Renee gets a crash card instead of the guide,
     * because an address was slightly malformed. The raw hash is a perfectly
     * good fallback: our own ids contain nothing that needs decoding.
     */
    let id = hash
    try {
      id = decodeURIComponent(hash)
    } catch {
      // Not a valid escape sequence. Take the address as typed.
    }
    const el = document.getElementById(id)
    if (el) el.scrollIntoView()
  }, [])

  const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  /**
   * Built from `hits.ids` rather than re-deriving the same set from `byId`.
   *
   * They agreed, so this was not a bug — but `ids` was computed on every
   * keystroke, asserted in the suite, and read by nothing. Coverage pointed at
   * code that does not ship is the quieter cousin of an assertion that cannot
   * fail, and the fix is to use the field rather than delete the tests.
   */
  const matched = useMemo(() => new Set(hits.ids), [hits])
  const shown = query ? headings.filter((h) => matched.has(h.id)) : headings
  /**
   * Sections, counting a `##` and not its `###` children.
   *
   * `shown` lists both, because a parent is credited with its subsections'
   * matches and both are worth offering as somewhere to click. But saying so in
   * the sentence produced arithmetic anybody can see is wrong: one mention
   * inside a subsection is one match in two listed entries, so "export button"
   * printed **"2 mentions in 3 sections"** — on the one control whose entire
   * job is being trustworthy about numbers.
   */
  const sectionCount = shown.filter((h) => h.level === 2).length || shown.length

  return (
    <div className="board guide" style={{ '--gscale': scale } as CSSProperties}>
      <div className="guideside">
        <div className="guidebar">
          <label className="gsearchwrap">
            <span className="gsearchlabel">Search the guide</span>
            <input
              className="gsearch"
              type="search"
              value={raw}
              placeholder="A word or two…"
              onChange={(e) => setRaw(e.target.value)}
            />
          </label>
          {/*
            What the search did, in words. A contents list that has quietly
            shrunk to three entries, with nothing saying why, reads as a page
            that has broken rather than one that has answered.
          */}
          {query && (
            <div className="gsearchsaid">
              {hits.total === 0
                ? 'No mention of that anywhere in the guide.'
                : shown.length === 0
                  ? /*
                      A match in the opening lines, which sit above the first
                      heading and belong to no section. It happens — the address
                      of the site is up there. Saying "in 0 sections" beside
                      "Nothing matches" beside a visible highlight was three
                      statements disagreeing with each other and with the page.
                    */
                    `${hits.total} ${hits.total === 1 ? 'mention' : 'mentions'}, in the opening lines at the top.`
                  : `${hits.total} ${hits.total === 1 ? 'mention' : 'mentions'} in ${sectionCount} ${
                      sectionCount === 1 ? 'section' : 'sections'
                    }. They are highlighted below.`}
            </div>
          )}
          {raw.length > 0 && !query && (
            <div className="gsearchsaid">Type a little more — at least three letters.</div>
          )}

          <div className="gsize">
            <span className="gsizelabel">Text size</span>
            <button
              className="btn ghost sm"
              onClick={() => setStep((s) => clampStep(s - 1))}
              disabled={clampStep(step) === 0}
              aria-label="Smaller text"
            >
              A−
            </button>
            <button
              className="btn ghost sm"
              onClick={() => setStep((s) => clampStep(s + 1))}
              disabled={clampStep(step) === READING_SIZES.length - 1}
              aria-label="Larger text"
            >
              A+
            </button>
          </div>
        </div>

        <details className="guidetoc" open={openToc} onToggle={(e) => setOpenToc(e.currentTarget.open)}>
          <summary>Contents{query && ` · ${shown.length}`}</summary>
          <nav>
            {shown.length === 0 && <div className="hint">Nothing matches.</div>}
            <ul>
              {shown.map((h) => (
                <li key={h.id} className={`l${h.level}${current === h.id ? ' here' : ''}`}>
                  <a href={`#${h.id}`}>
                    <span className="gt">{h.text}</span>
                    {query && hits.byId[h.id] > 0 && <span className="gn">{hits.byId[h.id]}</span>}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </details>
      </div>

      <div className="guidebody">
        <div className="notice">
          How the dashboard works, written down. Pearl has been given this same
          text, so you can also just ask her — the Pearl tab — and she will walk
          you through any of it.
        </div>

        {blocks.map((b, i) => (
          <Rendered key={i} block={b} id={idByAt.get(i)} query={query} />
        ))}
      </div>

      {scrolled && (
        <button className="btn sm gtop" onClick={toTop}>
          Back to top
        </button>
      )}
    </div>
  )
}

function Rendered({ block, id, query }: { block: Block; id?: string; query: string }) {
  if (block.kind === 'h1') return <h1>{inline(block.text, query)}</h1>
  if (block.kind === 'h2') return <h2 id={id}>{inline(block.text, query)}</h2>
  if (block.kind === 'h3') return <h3 id={id}>{inline(block.text, query)}</h3>
  if (block.kind === 'ul')
    return (
      <ul>
        {block.items.map((item, j) => (
          <li key={j}>{inline(item, query)}</li>
        ))}
      </ul>
    )
  return <p>{inline(block.text, query)}</p>
}

/**
 * **bold**, and the search highlight. Odd segments are the emphasised ones.
 *
 * The highlight is applied INSIDE each bold and non-bold run rather than across
 * the whole string, so a match that straddles a `**` boundary highlights the
 * part the reader can actually see. Doing it the other way round would need the
 * markers left in the text to keep the offsets honest, and they would then be
 * on screen.
 */
function inline(text: string, query: string) {
  return text.split('**').map((part, i) => {
    const marked = pieces(part, query).map((p, j) =>
      p.hit ? <mark key={j}>{p.text}</mark> : <span key={j}>{p.text}</span>,
    )
    return i % 2 === 1 ? <strong key={i}>{marked}</strong> : <span key={i}>{marked}</span>
  })
}
